import { useCallback, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { useSelector } from "react-redux";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FILTER_VALUE_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { ReportApi } from "go-report/core/services/types";
import { hasPermission, selectOrganization } from "go-security/services/organizations/selectors";
import { selectToken } from "go-security/services/session/selectors";
import { DashboardAlertApi, DashboardReportsApi } from "../../../../services/Api/Organization/types";
import { apiReport } from "../../../../services/Api/Report/apiReport";
import { api } from "../../../../services/Api/api";
import {
	parsePredefinedRangeToPreviousPeriodRange,
	processDashboardReportRequestParams,
} from "../../utils/reportConfigUtils";
import { DashboardFetchActions } from "../data/reducer";
import { ChartDataRangeType, DashboardType, FreshDashboardState } from "../types";
import useDashboardContext from "./useDashboardContext";

const getRidOfForbiddenAccessErrorForOrganizationDashboard = (err: any, dashboardType: DashboardType) => {
	let newErr = err;
	if (dashboardType === DashboardType.ORGANIZATION && err?.data?.errors) {
		newErr = { ...err };
		newErr.data.errors = newErr.data.errors.filter((error: Record<string, any>) => error?.code !== "access_denied");
	}
	return newErr;
};

const useDashboardApi = (): Record<string, any> => {
	const [loading, setLoading] = useState(false);
	const { addFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const token = useSelector(selectToken);
	const {
		state,
		state: {
			filtersConfig: {
				selectedFilter: { dateRange, organizationIds },
			},
			chartsConfig,
			chartsConfig: {
				reportCategory: { range: reportCategoryRange },
				reportPayment: { range: reportPaymentRange },
				reportProduct: { range: reportProductRange },
				reportSales: { range: reportSalestRange },
				reportSalesAdvanced: { range: reportSalesAdvancedRange },
			},
		},
		dashboardType,
		dispatch,
	} = useDashboardContext();
	const reportPermission = useSelector(hasPermission("VENUE_REPORTS_SHOW"));
	const freshDashboardState = useRef<FreshDashboardState | undefined>(undefined);
	const [rerenderDashboard, setRerenderDashboard] = useState<boolean>(true);
	const abortControllerRef = useRef<AbortController | undefined>(undefined);

	const fetchDashboardData = () => {
		return api.organization().getDashboard();
	};

	const fetchDashboardAlerts = useCallback((controller: AbortController): Promise<DashboardAlertApi> => {
		return api.organization().getDashboardAlerts(controller);
	}, []);

	const fetchDashboardReports = useCallback((controller: AbortController): Promise<DashboardReportsApi> => {
		return api.organization().getDashboardReports(controller);
	}, []);

	const fetchClientsInformation = useCallback(
		(controller: AbortController): Promise<AxiosResponse> | undefined => {
			if (!reportPermission && dashboardType === "ORGANIZATION") {
				return;
			}

			return apiReport.getReportsCustom(
				processDashboardReportRequestParams({
					defaultGroup: "NONE",
					chartType: "",
					organizationId: organization.id,
					type: "order",
					size: 6,
					dateRange: freshDashboardState.current?.selectedDateRange ?? dateRange,
					organizationIds: freshDashboardState.current?.selectedOrganizationIds ?? organizationIds,
					dashboardType,
					include: "AVERAGE_PER_GUESTS",
				}),
				{ controller }
			);
		},
		[organization.id, dashboardType, reportPermission, dateRange, organizationIds]
	);

	const fetchCallsReport = useCallback(
		(controller: AbortController): [Promise<AxiosResponse>, Promise<AxiosResponse>] | [undefined, undefined] => {
			if (!reportPermission && dashboardType === DashboardType.GROUP_ORGANIZATIONS) {
				return [undefined, undefined];
			}

			const reportDateRange = freshDashboardState.current?.selectedDateRange ?? dateRange;
			const reportConfig = {
				defaultGroup: "STATUS",
				chartType: "",
				organizationId: organization.id,
				size: 10000,
				dateRange: reportDateRange,
				organizationIds: freshDashboardState.current?.selectedOrganizationIds ?? organizationIds,
				dashboardType,
			};

			return [
				apiReport.getCallRecordsReport(processDashboardReportRequestParams(reportConfig), { controller }),
				apiReport.getCallRecordsReport(
					processDashboardReportRequestParams({
						...reportConfig,
						dateRange: parsePredefinedRangeToPreviousPeriodRange(reportDateRange, true) as string,
					}),
					{ controller }
				),
			];
		},
		[organization.id, dashboardType, reportPermission, dateRange, organizationIds]
	);

	const fetchPaymentReports = useCallback(
		(
			controller: AbortController,
			freshChartDateRangeFromEventHandler?: ChartDataRangeType
		): Promise<AxiosResponse<ReportApi>> | undefined => {
			if (!reportPermission && dashboardType === "ORGANIZATION") {
				return;
			}

			return apiReport.getReportsCustom(
				processDashboardReportRequestParams({
					defaultGroup: "PAYMENT_METHOD",
					chartType:
						freshChartDateRangeFromEventHandler ??
						freshDashboardState.current?.chartsConfig.reportPayment.range ??
						reportPaymentRange,
					organizationId: organization.id,
					type: "order_payment",
					size: 3,
					dateRange: freshDashboardState.current?.selectedDateRange ?? dateRange,
					organizationIds: freshDashboardState.current?.selectedOrganizationIds ?? organizationIds,
					dashboardType,
				}),
				{ controller }
			);
		},
		[organization.id, dashboardType, reportPermission, dateRange, organizationIds, reportPaymentRange]
	);

	const fetchCategoryReports = useCallback(
		(
			controller: AbortController,
			freshChartDateRangeFromEventHandler?: ChartDataRangeType
		): Promise<AxiosResponse<ReportApi>> | undefined => {
			if (!reportPermission && dashboardType === "ORGANIZATION") {
				return;
			}

			return apiReport.getReportsCustom(
				processDashboardReportRequestParams({
					defaultGroup: "PRODUCT_CATEGORY",
					chartType:
						freshChartDateRangeFromEventHandler ??
						freshDashboardState.current?.chartsConfig.reportCategory.range ??
						reportCategoryRange,
					organizationId: organization.id,
					type: "order_item",
					size: 6,
					dateRange: freshDashboardState.current?.selectedDateRange ?? dateRange,
					organizationIds: freshDashboardState.current?.selectedOrganizationIds ?? organizationIds,
					dashboardType,
				}),
				{ controller }
			);
		},
		[organization.id, dashboardType, reportPermission, dateRange, organizationIds, reportCategoryRange]
	);

	const fetchProductReports = useCallback(
		(
			controller: AbortController,
			freshChartDateRangeFromEventHandler?: ChartDataRangeType
		): Promise<AxiosResponse<ReportApi>> | undefined => {
			if (!reportPermission && dashboardType === "ORGANIZATION") {
				return;
			}

			return apiReport.getReportsCustom(
				processDashboardReportRequestParams({
					defaultGroup: "PRODUCT",
					chartType:
						freshChartDateRangeFromEventHandler ??
						freshDashboardState.current?.chartsConfig.reportProduct.range ??
						reportProductRange,
					organizationId: organization.id,
					type: "order_item",
					size: 6,
					dateRange: freshDashboardState.current?.selectedDateRange ?? dateRange,
					organizationIds: freshDashboardState.current?.selectedOrganizationIds ?? organizationIds,
					dashboardType,
				}),
				{ controller }
			);
		},
		[organization.id, dashboardType, reportPermission, organizationIds, dateRange, reportProductRange]
	);

	const fetchSalesReports = useCallback(
		(
			controller: AbortController,
			freshChartDateRangeFromEventHandler?: ChartDataRangeType
		): Promise<AxiosResponse<ReportApi>> | undefined => {
			if (!reportPermission && dashboardType === "ORGANIZATION") {
				return;
			}

			return apiReport.getReportsCustom(
				processDashboardReportRequestParams({
					defaultGroup: "ORDER_TYPE",
					chartType:
						freshChartDateRangeFromEventHandler ??
						freshDashboardState.current?.chartsConfig.reportSales.range ??
						reportSalestRange,
					organizationId: organization.id,
					type: "order",
					size: 6,
					dateRange: freshDashboardState.current?.selectedDateRange ?? dateRange,
					organizationIds: freshDashboardState.current?.selectedOrganizationIds ?? organizationIds,
					dashboardType,
				}),
				{ controller }
			);
		},
		[organization.id, dashboardType, reportPermission, dateRange, organizationIds, reportSalestRange]
	);

	const fetchSalesChartReports = useCallback(
		(
			controller: AbortController,
			freshChartDateRangeFromEventHandler?: ChartDataRangeType
		): Promise<AxiosResponse<ReportApi>> | undefined => {
			if (!reportPermission && dashboardType === "ORGANIZATION") {
				return;
			}

			return apiReport.getReportsCustom(
				processDashboardReportRequestParams({
					defaultGroup: "NONE",
					chartType:
						freshChartDateRangeFromEventHandler ??
						freshDashboardState.current?.chartsConfig.reportSalesAdvanced.range ??
						reportSalesAdvancedRange,
					organizationId: organization.id,
					type: "order",
					dateRange: freshDashboardState.current?.selectedDateRange ?? dateRange,
					organizationIds: freshDashboardState.current?.selectedOrganizationIds ?? organizationIds,
					include: "AVERAGE_PER_GUESTS",
					dashboardType,
				}),
				{ controller }
			);
		},
		[organization.id, dashboardType, reportPermission, dateRange, organizationIds, reportSalesAdvancedRange]
	);

	const getReportLastUpdateDate = useCallback(
		(controller: AbortController) => {
			if (!reportPermission && dashboardType === "ORGANIZATION") {
				return;
			}

			const URLPathname = window.location.pathname;
			const isGroupLocationsDashboard = URLPathname.includes("/dashboard");
			let organizationParam = `${NEW_WAY_TO_ENCODING_FILTER_SIGN}organization_id|e=`;
			if (isGroupLocationsDashboard) {
				let listOfOrganizations = state.filtersConfig.selectedFilter.organizationIds || "";
				listOfOrganizations = listOfOrganizations.replaceAll(",", FILTER_VALUE_SEPARATOR);
				organizationParam = `${organizationParam}${listOfOrganizations}`;
			} else {
				let selectedOrganization = URLPathname.substring(1);
				selectedOrganization = selectedOrganization.replaceAll(",", FILTER_VALUE_SEPARATOR);
				organizationParam = `${organizationParam}${selectedOrganization}`;
			}
			const params = {
				access_token: token,
				f: btoa(unescape(encodeURIComponent(organizationParam))),
				type: "ORDER",
			};
			return apiReport.getReportLastUpdateDate(params, controller);
		},
		[token, state.filtersConfig.selectedFilter.organizationIds, dashboardType, reportPermission]
	);

	const getTheOldestUpdate = (updates?: {
		organizations: { last_sale_sync_at: string; organization_id?: string }[];
	}): string | undefined => {
		if (!updates || updates?.organizations?.length === 0) return undefined;
		const updateDates = updates?.organizations
			?.filter((update) => update.last_sale_sync_at)
			.map((update) => new Date(update.last_sale_sync_at).getTime());
		const oldestTimestamp = Math.min(...updateDates);
		return new Date(oldestTimestamp).toISOString();
	};

	const fetchData = useCallback(
		async (controller: AbortController) => {
			setLoading(true);
			setRerenderDashboard(true);

			try {
				let response, data: Record<string, any>;

				if (dashboardType === DashboardType.ORGANIZATION) {
					response = await Promise.all([
						fetchDashboardAlerts(controller),
						fetchDashboardReports(controller),
						fetchPaymentReports(controller),
						fetchProductReports(controller),
						fetchCategoryReports(controller),
						fetchSalesReports(controller),
						fetchSalesChartReports(controller),
						fetchClientsInformation(controller),
						...(fetchCallsReport(controller) ?? []),
						getReportLastUpdateDate(controller),
					]);

					const [
						alerts,
						reports,
						payment_reports,
						product_reports,
						category_reports,
						sales_reports,
						sales_advanced_reports,
						clients_informations,
						callsReportCurrentDateRange,
						callsReportPreviousDateRange,
						last_update,
					] = response;

					const lastUpdateValue = last_update?.organizations[0]?.last_sale_sync_at;

					data = {
						alerts,
						reports,
						payment_reports: payment_reports?.data,
						product_reports: product_reports?.data,
						category_reports: category_reports?.data,
						sales_reports: sales_reports?.data,
						sales_advanced_reports: sales_advanced_reports?.data,
						clients_informations: clients_informations?.data,
						callsReportCurrentDateRange: callsReportCurrentDateRange?.data.data,
						callsReportPreviousDateRange: callsReportPreviousDateRange?.data.data,
						last_update: lastUpdateValue,
					};
				} else {
					response = await Promise.all([
						fetchPaymentReports(controller),
						fetchProductReports(controller),
						fetchCategoryReports(controller),
						fetchSalesReports(controller),
						fetchSalesChartReports(controller),
						fetchClientsInformation(controller),
						getReportLastUpdateDate(controller),
					]);

					const [
						payment_reports,
						product_reports,
						category_reports,
						sales_reports,
						sales_advanced_reports,
						clients_informations,
						last_update,
					] = response;

					const lastUpdateValue = getTheOldestUpdate(last_update);

					data = {
						payment_reports: payment_reports?.data,
						product_reports: product_reports?.data,
						category_reports: category_reports?.data,
						sales_reports: sales_reports?.data,
						sales_advanced_reports: sales_advanced_reports?.data,
						clients_informations: clients_informations?.data,
						last_update: lastUpdateValue,
					};
				}

				return data;
			} catch (err: any) {
				const newErr = getRidOfForbiddenAccessErrorForOrganizationDashboard(err, dashboardType);
				handleError.alert(newErr, addFlash);
				return undefined;
			} finally {
				setLoading(false);
				setRerenderDashboard(false);
				freshDashboardState.current = undefined;
			}
		},
		[
			addFlash,
			fetchDashboardAlerts,
			fetchDashboardReports,
			fetchPaymentReports,
			fetchProductReports,
			fetchCategoryReports,
			fetchSalesReports,
			fetchSalesChartReports,
			dashboardType,
			getReportLastUpdateDate,
			fetchClientsInformation,
		]
	);

	const fetchDataAndApplyNewDataToDashboardState = useCallback(
		async (freshDashboardStateComingFromEventHandler?: FreshDashboardState) => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			abortControllerRef.current = new AbortController();

			if (freshDashboardStateComingFromEventHandler) {
				freshDashboardState.current = {
					selectedDateRange: freshDashboardStateComingFromEventHandler.selectedDateRange ?? dateRange,
					selectedOrganizationIds:
						freshDashboardStateComingFromEventHandler.selectedOrganizationIds ?? organizationIds,
					chartsConfig: freshDashboardStateComingFromEventHandler.chartsConfig ?? chartsConfig,
				};
			}

			const fetchedDashboardData = await fetchData(abortControllerRef.current);

			if (fetchedDashboardData) {
				dispatch({
					type: DashboardFetchActions.FETCH_All_DATA,
					payload: {
						data: fetchedDashboardData,
						shouldOverrideExistingData: fetchedDashboardData === undefined,
					},
				});
			}
		},
		[JSON.stringify(chartsConfig), dateRange, dispatch, fetchData, organizationIds]
	);

	return {
		fetchDashboardAlerts,
		fetchDashboardReports,
		fetchDashboardData,
		loading,
		fetchPaymentReports,
		fetchCategoryReports,
		fetchProductReports,
		fetchSalesReports,
		fetchSalesChartReports,
		rerenderDashboard,
		fetchDataAndApplyNewDataToDashboardState,
	};
};
export default useDashboardApi;
